import React, { useMemo, useState } from 'react';
import { Modal, Button, Form, Input } from 'components';
import isEmail from 'validator/lib/isEmail';
import isMobilePhone from 'validator/lib/isMobilePhone';

type AddNewTeamModalProps = {
  visible: boolean;
  onCancel: () => void;
  onSave: (payload: { name: string; email: string; phone: string }) => void;
};

const AddNewTeamModal = ({
  visible,
  onCancel,
  onSave,
}: AddNewTeamModalProps) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const isMobileValid = useMemo(
    () => (mobile ? isMobilePhone(mobile) : true),
    [mobile],
  );

  const isEmailValid = React.useMemo(
    () => (email ? isEmail(email) : true),
    [email],
  );

  const handleCancelClick = () => {
    setName('');
    setEmail('');
    setMobile('');
    onCancel();
  };

  const handleSaveClick = () => {
    onSave({ name, email, phone: mobile });
    setName('');
    setEmail('');
    setMobile('');
  };

  return (
    <Modal
      open={visible}
      title="Add Team"
      onCancel={handleCancelClick}
      footer={[
        <Button
          key="cancel"
          data-cy="cancel-button"
          onClick={handleCancelClick}
        >
          Cancel
        </Button>,
        <Button
          type="primary"
          key="save"
          data-cy="save-button"
          onClick={handleSaveClick}
          disabled={
            !name || !email || !mobile || !isMobileValid || !isEmailValid
          }
        >
          Save
        </Button>,
      ]}
    >
      <Form layout="vertical">
        <Form.Item>
          <Input
            data-cy="new-team-input-name"
            placeholder="Name"
            value={name}
            label="Name"
            required
            onChange={({ target: { value } }) => setName(value)}
          />
        </Form.Item>
        <Form.Item
          validateStatus={!isEmailValid ? 'error' : undefined}
          help={isEmailValid ? '' : 'Please provide a valid email.'}
        >
          <Input
            type="email"
            data-cy="new-team-input-email"
            label="Email"
            placeholder="Email"
            required
            value={email}
            onChange={({ target: { value } }) => setEmail(value)}
          />
        </Form.Item>
        <Form.Item
          validateStatus={!isMobileValid ? 'error' : undefined}
          help={isMobileValid ? '' : 'Please provide a valid phone number.'}
        >
          <Input
            data-cy="new-team-input-phonenumber"
            placeholder="Phone Number"
            label="Phone Number"
            value={mobile}
            required
            phoneNumberMask
            onChange={({ target: { value } }) => setMobile(value)}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddNewTeamModal;
